import React from 'react';
import {
  Box,
  Chip,
  Container,
  Grid,
  Paper,
  Stack,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Badge as BadgeIcon,
  Business as BusinessIcon,
  TrendingUp as TrendingUpIcon,
  Groups as GroupsIcon,
} from '@mui/icons-material';
import MainLayout from '../../../components/layout/MainLayout';
import { staffApi, Staff, Department, Designation } from '../api';

const statusColor = (status?: string) => {
  const code = String(status || '').toUpperCase();
  if (code === 'ACTIVE') return 'success';
  if (code === 'RESIGNED') return 'warning';
  if (code === 'INACTIVE') return 'default';
  return 'info';
};

const SectionCard: React.FC<{ title: string; subtitle?: string; icon?: React.ReactNode; children: React.ReactNode }> = ({ title, subtitle, icon, children }) => (
  <Paper sx={{ p: 2.5, borderRadius: 3, height: '100%' }}>
    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.5 }}>
      {icon}
      <Box>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>{title}</Typography>
        {subtitle ? <Typography variant="body2" color="text.secondary">{subtitle}</Typography> : null}
      </Box>
    </Stack>
    {children}
  </Paper>
);

const StaffDashboard: React.FC = () => {
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState('');
  const [staff, setStaff] = React.useState<Staff[]>([]);
  const [departments, setDepartments] = React.useState<Department[]>([]);
  const [designations, setDesignations] = React.useState<Designation[]>([]);
  const [resigned, setResigned] = React.useState<Staff[]>([]);

  React.useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const [staffRows, deptRows, desigRows, resignedRows] = await Promise.all([
          staffApi.listStaff({ includeResigned: 'false' }).catch(() => []),
          staffApi.listDepartments().catch(() => []),
          staffApi.listDesignations().catch(() => []),
          staffApi.listResigned().catch(() => []),
        ]);

        setStaff(staffRows);
        setDepartments(deptRows);
        setDesignations(desigRows);
        setResigned(resignedRows);
      } catch {
        setError('Staff module data could not be loaded.');
      } finally {
        setLoading(false);
      }
    };

    load();
  }, []);

  const activeStaff = staff.filter((row) => String(row.status).toUpperCase() === 'ACTIVE').length;
  const resignedCount = resigned.length;
  const departmentsWithCount = departments.filter((d) => (d.staffCount || 0) > 0).length;

  return (
    <MainLayout>
      <Container maxWidth="xl" sx={{ py: 3 }}>
        <Paper
          sx={{
            p: 3,
            mb: 3,
            borderRadius: 4,
            background: 'linear-gradient(135deg, #0f172a 0%, #1d4ed8 55%, #0ea5e9 100%)',
            color: '#fff',
          }}
        >
          <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={2} alignItems={{ xs: 'flex-start', sm: 'center' }}>
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 800 }}>Staff Management Dashboard</Typography>
              <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5 }}>
                ERP-grade staff lifecycle management, attendance, documents, reporting, and payroll readiness.
              </Typography>
            </Box>
            <Stack direction="row" spacing={1} flexWrap="wrap">
              <Chip icon={<BadgeIcon />} label={`${activeStaff} Active`} sx={{ bgcolor: 'rgba(255,255,255,0.15)', color: '#fff' }} />
              <Chip icon={<BusinessIcon />} label={`${departmentsWithCount} Departments`} sx={{ bgcolor: 'rgba(255,255,255,0.15)', color: '#fff' }} />
            </Stack>
          </Stack>
        </Paper>

        {error ? <Alert severity="warning" sx={{ mb: 2 }}>{error}</Alert> : null}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={6} lg={3}>
                <SectionCard title="Staff" subtitle="Active profiles" icon={<BadgeIcon color="primary" />}>
                  <Typography variant="h3" sx={{ fontWeight: 800 }}>{activeStaff}</Typography>
                </SectionCard>
              </Grid>
              <Grid item xs={12} sm={6} lg={3}>
                <SectionCard title="Departments" subtitle="Organizational units" icon={<BusinessIcon color="primary" />}>
                  <Typography variant="h3" sx={{ fontWeight: 800 }}>{departments.length}</Typography>
                </SectionCard>
              </Grid>
              <Grid item xs={12} sm={6} lg={3}>
                <SectionCard title="Designations" subtitle="Role hierarchy" icon={<GroupsIcon color="primary" />}>
                  <Typography variant="h3" sx={{ fontWeight: 800 }}>{designations.length}</Typography>
                </SectionCard>
              </Grid>
              <Grid item xs={12} sm={6} lg={3}>
                <SectionCard title="Resigned" subtitle="Historical records" icon={<TrendingUpIcon color="primary" />}>
                  <Typography variant="h3" sx={{ fontWeight: 800 }}>{resignedCount}</Typography>
                </SectionCard>
              </Grid>
            </Grid>

            <Grid container spacing={2}>
              <Grid item xs={12} lg={8}>
                <SectionCard title="Staff Snapshot" subtitle="Current active staff" icon={<BadgeIcon color="primary" />}>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Code</TableCell>
                          <TableCell>Name</TableCell>
                          <TableCell>Department</TableCell>
                          <TableCell>Designation</TableCell>
                          <TableCell>Status</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {staff.slice(0, 8).map((row) => (
                          <TableRow key={row.id} hover>
                            <TableCell>{row.employeeCode}</TableCell>
                            <TableCell>{row.firstName} {row.lastName}</TableCell>
                            <TableCell>{row.department?.name || '-'}</TableCell>
                            <TableCell>{row.designation?.name || '-'}</TableCell>
                            <TableCell><Chip size="small" label={row.status} color={statusColor(row.status) as any} /></TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </SectionCard>
              </Grid>
              <Grid item xs={12} lg={4}>
                <SectionCard title="Quick Info" subtitle="Module overview" icon={<TrendingUpIcon color="primary" />}>
                  <Stack spacing={1}>
                    <Box>
                      <Typography variant="body2" color="text.secondary">Total Staff</Typography>
                      <Typography variant="h6" sx={{ fontWeight: 700 }}>{staff.length}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="body2" color="text.secondary">Active %</Typography>
                      <Typography variant="h6" sx={{ fontWeight: 700 }}>
                        {staff.length > 0 ? ((activeStaff / staff.length) * 100).toFixed(1) : 0}%
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="body2" color="text.secondary">Avg Designation per Department</Typography>
                      <Typography variant="h6" sx={{ fontWeight: 700 }}>
                        {departments.length > 0 ? (designations.length / departments.length).toFixed(1) : 0}
                      </Typography>
                    </Box>
                  </Stack>
                </SectionCard>
              </Grid>
            </Grid>
          </>
        )}
      </Container>
    </MainLayout>
  );
};

export default StaffDashboard;
